/**
 * `/settings/approvals` — **who approves money** (C11, FR-A8 §2; FR-A5,
 * ADR-0009 §6; T-1.18).
 *
 * Issuing, crediting and refunding each need a role as well as the
 * permission. **Unset is the partner** — the most privileged answer, not the
 * most convenient — and the page says so on every row.
 *
 * The three keys are `app/api/billing.py`'s `ISSUE_ACTION`, `CREDIT_ACTION`
 * and `REFUND_ACTION`. A key spelled differently here is a firm's
 * configuration that silently stops applying to the action it was set for.
 */

import { Alert, Select, Skeleton } from '@fiveox/ui'
import { ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { EditBar, useLeaveGuard } from '../../../components/edit'
import { SETTINGS, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { firm } from '../../../lib/api/firm'
import { SettingsPage, SettingsSection } from './SettingsPage'

export const MONEY_ACTIONS: Array<{ key: string; label: string; hint: string }> = [
  {
    key: 'invoice:issue',
    label: 'Issue an invoice',
    hint: 'Takes the next number in your series and posts to the client’s ledger. It cannot be undone.',
  },
  {
    key: 'invoice:credit',
    label: 'Credit an invoice',
    hint: 'A credit note covering every line cancels the invoice. Also irreversible.',
  },
  {
    key: 'refund:approve',
    label: 'Refund a receipt',
    hint: 'Money leaving the firm. Where it reverses an invoiced charge it issues a credit note too.',
  },
]

/** The roles `app/rbac.py` registers that may hold a money verb, in seniority
 *  order. Listed here because the settings API does not serve the role list. */
export const APPROVER_ROLES: Array<{ code: string; name: string }> = [
  { code: 'partner', name: 'Partner' },
  { code: 'manager', name: 'Practice manager' },
  { code: 'reviewer', name: 'Reviewer' },
  { code: 'associate', name: 'Associate' },
]

/** The mapping with unchosen actions **absent**, never `""`: "not
 *  configured" is a real state the server resolves to the partner, and an
 *  empty string would be a role code nobody holds. */
export function withApprover(
  roles: Record<string, string>,
  action: string,
  role: string,
): Record<string, string> {
  const next = { ...roles }
  if (role) next[action] = role
  else delete next[action]
  return next
}

export function Approvals() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [original, setOriginal] = useState<Record<string, string> | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    firm
      .settings()
      .then((body) => {
        setOriginal(body.data.approver_roles ?? {})
        setDraft(body.data.approver_roles ?? {})
      })
      .catch(() => setError('Who approves money could not be loaded.'))
  }, [])

  const dirty = useMemo(
    () =>
      original !== null &&
      MONEY_ACTIONS.some((action) => (original[action.key] ?? '') !== (draft[action.key] ?? '')),
    [original, draft],
  )
  const guard = useLeaveGuard(dirty)

  const save = async () => {
    if (!original) return
    setSaving(true)
    setError(null)
    try {
      // Unknown keys the server holds are kept — this page only owns three.
      const body = await firm.updateSettings({ approver_roles: { ...draft } })
      setOriginal(body.data.approver_roles ?? {})
      setDraft(body.data.approver_roles ?? {})
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your changes could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsPage
      title="Approvals"
      description="Who may issue, credit and refund. Unset, it is the partner."
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}
      {original === null ? (
        <Skeleton className="h-72 w-full rounded-2xl" />
      ) : (
        <>
          <SettingsSection
            icon={<ShieldCheck size={18} aria-hidden />}
            title="Who approves money"
            description="Each needs this role as well as the permission to act."
          >
            <ul className="divide-border divide-y">
              {MONEY_ACTIONS.map((action) => (
                <li
                  key={action.key}
                  className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_16rem] sm:items-center"
                >
                  <div>
                    <p className="text-foreground text-sm font-medium">{action.label}</p>
                    <p className="text-muted-foreground mt-0.5 text-sm">{action.hint}</p>
                  </div>
                  <Select
                    aria-label={`Who approves: ${action.label}`}
                    value={draft[action.key] ?? ''}
                    disabled={!editable}
                    onChange={(e) => setDraft(withApprover(draft, action.key, e.target.value))}
                  >
                    <option value="">Partner (not chosen)</option>
                    {APPROVER_ROLES.map((role) => (
                      <option key={role.code} value={role.code}>
                        {role.name}
                      </option>
                    ))}
                  </Select>
                </li>
              ))}
            </ul>
          </SettingsSection>
          <Alert tone="info">
            {/* Spec §9-19: `evaluate` returns a union of verbs and never says
                which roles produced it, so the check is exact at the narrow
                end and generous at the top. */}
            A partner satisfies every one of these, whichever you choose. ProDesk reads permissions
            from the platform one person at a time and is told what they may do, not which role they
            hold — so the check is exact for the role you name and always passes for a partner.
          </Alert>
          <EditBar
            dirty={dirty}
            saving={saving}
            editable={editable}
            onSave={() => void save()}
            onDiscard={() => setDraft(original)}
          />
        </>
      )}
    </SettingsPage>
  )
}
